/**
 * Custom select — progressive enhancement over een native <select>.
 *
 * Generiek en herbruikbaar: JS zoekt `[data-custom-select]`-wrappers, bouwt per
 * wrapper een toegankelijke listbox-widget (button + optie-lijst) en houdt de
 * native <select> als échte form-control in de DOM (de GET-submit blijft werken).
 * Zonder JS werkt de native select gewoon (no-JS fallback). Self-init op DOM-ready,
 * meerdere instances, no-op zonder doelen. Import in resources/js/main.js.
 *
 * Toegankelijk (ARIA-listbox): de button heeft aria-haspopup/expanded/controls; de
 * lijst role="listbox" met aria-activedescendant; opties role="option" +
 * aria-selected. Toetsenbord: Enter/Space/pijlen openen/navigeren/kiezen, Escape
 * sluit; click-outside sluit. Bij een keuze wordt de native select geüpdatet en een
 * 'change'-event gedispatcht.
 */

let agoraSelectUid = 0;

/**
 * Enhancet één [data-custom-select]-wrapper (no-op als er geen bruikbare native
 * select is of de wrapper al enhanced is).
 *
 * @param {HTMLElement} wrapper - De wrapper met de native <select>.
 */
function enhance(wrapper) {
    const native = wrapper.querySelector('select');
    if (!native || wrapper.classList.contains('is-enhanced')) {
        return;
    }

    // Alleen echte opties komen in de widget-lijst; placeholder-opties worden geweerd
    // (lege value, disabled, of Formidable's data-placeholder="true"). De placeholder
    // blijft wél de button-tekst zolang de native select erop staat (zie hieronder) —
    // en blijft in de native select voor de lege submit-waarde/validatie.
    const isPlaceholder = (option) =>
        option.value === '' || option.disabled || option.dataset.placeholder === 'true';
    const options = Array.from(native.options).filter((option) => !isPlaceholder(option));
    if (options.length === 0) {
        return;
    }

    agoraSelectUid += 1;
    const listId = `custom-select-list-${agoraSelectUid}`;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-select__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', listId);

    const value = document.createElement('span');
    value.className = 'custom-select__value';
    button.appendChild(value);

    const chevron = document.createElement('span');
    chevron.className = 'custom-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    button.appendChild(chevron);

    const list = document.createElement('ul');
    list.className = 'custom-select__list';
    list.id = listId;
    list.tabIndex = -1;
    list.hidden = true;
    list.setAttribute('role', 'listbox');

    const items = options.map((option, index) => {
        const item = document.createElement('li');
        item.className = 'custom-select__option';
        item.id = `${listId}-option-${index}`;
        item.dataset.value = option.value;
        item.textContent = option.textContent.trim();
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', native.value === option.value ? 'true' : 'false');
        list.appendChild(item);
        return item;
    });

    const selected = native.options[native.selectedIndex] || null;
    value.textContent = selected ? selected.textContent.trim() : '';

    wrapper.appendChild(button);
    wrapper.appendChild(list);
    wrapper.classList.add('is-enhanced');
    native.setAttribute('tabindex', '-1');
    native.setAttribute('aria-hidden', 'true');

    let activeIndex = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    if (activeIndex < 0) {
        activeIndex = 0;
    }

    const setActive = (index) => {
        activeIndex = index;
        items.forEach((item, i) => item.classList.toggle('is-active', i === index));
        list.setAttribute('aria-activedescendant', items[index].id);
    };

    const isOpen = () => !list.hidden;

    const open = () => {
        list.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        setActive(activeIndex);
        list.focus();
    };

    const close = (focusButton) => {
        list.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        list.removeAttribute('aria-activedescendant');
        if (focusButton) {
            button.focus();
        }
    };

    const choose = (index) => {
        const option = options[index];
        native.value = option.value;
        native.dispatchEvent(new Event('change', { bubbles: true }));
        value.textContent = option.textContent.trim();
        items.forEach((item, i) =>
            item.setAttribute('aria-selected', i === index ? 'true' : 'false'),
        );
        close(true);
    };

    button.addEventListener('click', () => (isOpen() ? close(true) : open()));

    button.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
            open();
        }
    });

    list.addEventListener('keydown', (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActive(Math.min(items.length - 1, activeIndex + 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActive(Math.max(0, activeIndex - 1));
                break;
            case 'Home':
                event.preventDefault();
                setActive(0);
                break;
            case 'End':
                event.preventDefault();
                setActive(items.length - 1);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                choose(activeIndex);
                break;
            case 'Escape':
                event.preventDefault();
                close(true);
                break;
            case 'Tab':
                close(false);
                break;
            default:
                break;
        }
    });

    items.forEach((item, index) => {
        item.addEventListener('click', () => choose(index));
        item.addEventListener('mousemove', () => setActive(index));
    });

    document.addEventListener('click', (event) => {
        if (isOpen() && !wrapper.contains(event.target)) {
            close(false);
        }
    });

    // Error-state gelijkhouden aan de aria-invalid van de native select (Formidable
    // zet die bij validatie); voor blok-selects blijft 'ie altijd false → no-op.
    watchInvalid(native, wrapper);
}

/**
 * Houdt de error-class (custom-select--error) van de widget gelijk aan de
 * aria-invalid van de native select. Reageert live via een attribute-observer,
 * zonder afhankelijkheid van Formidable-interne events.
 *
 * @param {HTMLSelectElement} native  - De native select.
 * @param {HTMLElement}       wrapper - De custom-select-wrapper.
 */
function watchInvalid(native, wrapper) {
    const sync = () => {
        const invalid = native.getAttribute('aria-invalid') === 'true';
        wrapper.classList.toggle('custom-select--error', invalid);
    };

    sync();

    if (typeof MutationObserver !== 'undefined') {
        new MutationObserver(sync).observe(native, {
            attributes: true,
            attributeFilter: ['aria-invalid'],
        });
    }
}

/**
 * Enhancet één Formidable-select: wrap 'm in een eigen .custom-select en draai de
 * gedeelde enhance(). De native select blijft binnen .frm_form_field in de DOM (dus
 * Formidable's submit + conditional logic werken door). Idempotent + veilig: geen
 * [multiple], geen select buiten een Formidable-veld, geen al-gewrapte select.
 *
 * @param {Element} native - Een kandidaat-select.
 */
function enhanceFormidableSelect(native) {
    if (
        !(native instanceof HTMLSelectElement) ||
        native.multiple ||
        !native.closest('.frm_form_field') ||
        native.closest('.custom-select')
    ) {
        return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'custom-select custom-select--frm';
    native.parentNode.insertBefore(wrapper, native);
    wrapper.appendChild(native);
    native.classList.add('custom-select__native');

    enhance(wrapper);
}

/**
 * Enhancet alle Formidable-selects binnen een scope.
 *
 * @param {ParentNode} scope - Zoekwortel (default: document).
 */
export function initFormidableSelects(scope = document) {
    scope.querySelectorAll('.frm_form_field select').forEach(enhanceFormidableSelect);
}

let agoraFrmObserver = null;

/**
 * Observeert dynamisch toegevoegde Formidable-selects (conditional logic,
 * paginawissel, repeaters) en enhancet ze alsnog. Alleen actief als er een
 * Formidable-form op de pagina staat (anders geen observer → geen overhead). De
 * enhancer is idempotent, dus onze eigen DOM-mutaties veroorzaken geen dubbeling.
 */
function observeFormidableSelects() {
    if (
        agoraFrmObserver ||
        typeof MutationObserver === 'undefined' ||
        !document.body ||
        !document.querySelector('.frm_form_field, .frm_forms')
    ) {
        return;
    }

    agoraFrmObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType !== 1) {
                    return;
                }
                if (node.tagName === 'SELECT') {
                    enhanceFormidableSelect(node);
                }
                if (typeof node.querySelectorAll === 'function') {
                    node.querySelectorAll('select').forEach(enhanceFormidableSelect);
                }
            });
        });
    });

    agoraFrmObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Enhancet alle custom-selects binnen een scope.
 *
 * @param {ParentNode} scope - Zoekwortel (default: document).
 */
export function initCustomSelect(scope = document) {
    scope.querySelectorAll('[data-custom-select]').forEach(enhance);
}

/**
 * Draait beide enhancers (blok-selects + Formidable-selects) + start de observer
 * voor dynamisch toegevoegde Formidable-selects.
 */
function initAll() {
    initCustomSelect();
    initFormidableSelects();
    observeFormidableSelects();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
} else {
    initAll();
}
